import React from "react";
import { motion } from "framer-motion";
import { GlassWater } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { DRINKS, DRINKS_IMG } from "@/data/site";
import { Reveal, Kicker } from "./Reveal";
import { openWhatsApp } from "@/lib/wa";

export default function Drinks() {
  const { t, lang } = useLang();
  return (
    <section id="drinks" className="relative overflow-hidden py-24 md:py-32" data-testid="drinks-section">
      <div className="pointer-events-none absolute inset-0 opacity-30 [background:radial-gradient(600px_420px_at_15%_50%,rgba(139,0,0,0.18),transparent),radial-gradient(520px_420px_at_85%_60%,rgba(212,175,55,0.1),transparent)]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 md:px-10 lg:grid-cols-2">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
          data-testid="drinks-image"
        >
          <div className="relative overflow-hidden rounded-[3px] ring-1 ring-[var(--gold)]/25">
            <img
              src={DRINKS_IMG}
              alt="Bissap et jus de gingembre maison"
              className="aspect-[4/3] w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
          <span className="pointer-events-none absolute -bottom-3 -left-3 h-16 w-16 border-b border-l border-[var(--gold)]/40" />
          <span className="pointer-events-none absolute -right-3 -top-3 h-16 w-16 border-r border-t border-[var(--gold)]/40" />
        </motion.div>

        {/* Copy + drinks */}
        <div>
          <Kicker className="mb-5">{t.drinksKicker}</Kicker>
          <h2 className="font-serif text-5xl leading-[0.95] tracking-tight text-[var(--text)] md:text-6xl">
            {t.drinksTitle} <span className="italic gold-gradient-text">{t.drinksTitleEm}</span>
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-md font-sans text-[15px] leading-relaxed text-[var(--text-2)]">
              {t.drinksIntro}
            </p>
          </Reveal>

          <div className="mt-8 space-y-4">
            {DRINKS.map((d, i) => (
              <motion.button
                key={d.id}
                onClick={() => openWhatsApp(`${t.waOrder} (${d.name})`)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`drink-${d.id}`}
                className="group flex w-full items-center gap-4 border-b border-[var(--gold)]/15 pb-4 text-left transition-colors hover:border-[var(--gold)]/50"
              >
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border"
                  style={{ borderColor: d.accent, color: d.accent }}
                >
                  <GlassWater size={18} />
                </span>
                <span className="flex-1">
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="font-serif text-2xl text-[var(--text)] group-hover:text-[var(--gold-light)]">{d.name}</span>
                    <span className="font-serif text-2xl font-bold text-[var(--gold)]">{d.price}</span>
                  </span>
                  <span className="mt-1 block font-sans text-[13px] leading-relaxed text-[var(--text-2)]">
                    {lang === "fr" ? d.fr : d.en}
                  </span>
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
